import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import groupsServices from '@src/services/groups.service';

export const getGroupsList = createAsyncThunk(
  'getGroupsList',
  async (body, { rejectWithValue }) => {
    try {
      return await groupsServices.getGroupsList();
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getRoleList = createAsyncThunk('getRoleList', async (body, { rejectWithValue }) => {
  try {
    return await groupsServices.getRoleList();
  } catch (error) {
    return rejectWithValue(error?.data);
  }
});

export const getGroupClaims = createAsyncThunk(
  'getGroupClaims',
  async ({ id }, { rejectWithValue }) => {
    try {
      return await groupsServices.getGroupClaims({ id });
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const addGroup = createAsyncThunk(
  'addGroup',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await groupsServices.addGroup(data);
      dispatch(getGroupsList());
      return response;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const deleteGroup = createAsyncThunk('deleteGroup', async (data, { rejectWithValue }) => {
  try {
    const response = await groupsServices.deleteGroup(data);
    return response;
  } catch (error) {
    return rejectWithValue(error?.data);
  }
});

export const updateGroup = createAsyncThunk(
  'updateGroup',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await groupsServices.updateGroup(data);
      dispatch(getGroupsList());
      return response;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const addGroupClaims = createAsyncThunk(
  'addGroupClaims',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await groupsServices.addGroupClaims(data?.entity);
      dispatch(getGroupsList());
      return response;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const deleteGroupClaims = createAsyncThunk(
  'deleteGroupClaims',
  async ({ id }, { rejectWithValue }) => {
    try {
      return await groupsServices.deleteGroupClaims({ id });
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getAllOperationClaims = createAsyncThunk(
  'getAllOperationClaims',
  async ({ body, pageNumber, pageSize }, { rejectWithValue }) => {
    try {
      const result = await groupsServices.getAllClaims({ body, pageNumber, pageSize });
      return {
        ...result,
        ...body,
      };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

const initialState = {
  claims: [],
  roleList: [],
  groupList: [],
  groupClaims: [],
  claimList: {},
  newClaimList: {},
  selectedRole: null,
  selectedMenuType: null,
  draftedTableProperty: {
    currentPage: 1,
    page: 1,
    pageSize: 10,
    totalCount: 0,
  },
};

export const groupsSlice = createSlice({
  name: 'groups',
  initialState,
  reducers: {
    selectedGroup: (state, action) => {
      state.selectedRole = action.payload;
    },
    selectedMenuList: (state, action) => {
      state.selectedMenuType = action.payload;
    },
    resetClaimsList: (state) => {
      state.groupClaims = [];
    },
    resetRole: (state) => {
      state.selectedRole = null;
    },
    setClaimList: (state, action) => {
      state.claimList = action.payload;
    },
    setNewClaimList: (state, action) => {
      state.newClaimList = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getGroupsList.fulfilled, (state, action) => {
      state.groupList = action?.payload?.data;
      state.draftedTableProperty = {
        currentPage: 1,
        page: 1,
        pageSize: 10,
        totalCount: action?.payload?.data?.length,
      };
    });
    builder.addCase(getGroupsList.rejected, (state, action) => {
      state.groupList = [];
      state.draftedTableProperty = {
        currentPage: 1,
        page: 1,
        pageSize: 10,
        totalCount: 0,
      };
    });
    builder.addCase(getGroupClaims.fulfilled, (state, action) => {
      state.groupClaims = action?.payload?.data;
    });
    builder.addCase(getRoleList.fulfilled, (state, action) => {
      state.roleList = action?.payload?.data;
    });
    builder.addCase(getAllOperationClaims.fulfilled, (state, action) => {
      state.claims = {
        ...state.claims,
        [action.payload.vouId]: {
          data: action?.payload?.data?.items || [],
          pagedProperty: action?.payload?.data?.pagedProperty,
        },
      };
      const vouId = action?.meta?.arg?.body?.vouId;
      const claims =
        action?.payload?.data?.items
          ?.map((claim) => {
            return claim?.isCheck && claim.id;
          })
          .filter((item) => item) || [];

      const tempClaim = claims.concat(
        state.claimList[vouId]?.filter((item) => claims.indexOf(item) < 0),
      );
      state.claimList = {
        ...state.claimList,
        [vouId]: tempClaim.filter((item) => item),
      };

      state.newClaimList = {
        ...state.newClaimList,
        [vouId]: [...(state.newClaimList[vouId] || [])],
      };
    });
  },
});

export const {
  selectedGroup,
  selectedMenuList,
  resetClaimsList,
  resetRole,
  setClaimList,
  setNewClaimList,
} = groupsSlice.actions;
